import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_FEEDBACK } from '@ctw/shared/utils/query-keys';
import { useCallback, useState } from 'react';
import type { FeedbackRequest, FeedbackResponse } from './types';

interface SubmitFeedbackArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  requestData: FeedbackRequest;
}

const submitFeedback = async ({ ctwFetch, requestContext, requestData }: SubmitFeedbackArgs) => {
  const endpoint = `${getAiServiceBaseUrl({ env: requestContext.env })}/feedback`;
  const createdAt = new Date().toISOString();
  const userId = requestContext.authTokenState.email;

  const finalRequestData = {
    ...requestData,
    createdAt,
    userId,
  };

  const response = await ctwFetch(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(finalRequestData),
  });
  return response.data as FeedbackResponse;
};

export const useFeedback = (requestData: FeedbackRequest) => {
  const [isEnabled, setIsEnabled] = useState(false);

  const query = useCtwQuery({
    queryId: QUERY_KEY_AI_FEEDBACK,
    queryKey: [JSON.stringify(requestData)],
    queryFn: async ({ requestContext, ctwFetch }) => {
      try {
        return await submitFeedback({
          ctwFetch,
          requestContext,
          requestData,
        });
      } catch (e) {
        throw new Error(`Failed to fetch feedback: ${e}`);
      }
    },
    enabled: isEnabled,
  });

  const triggerFeedback = useCallback(() => {
    setIsEnabled(true);
    return query.refetch();
  }, [query]);

  return { ...query, triggerFeedback };
};
